/**
 * PromoCode Component
 * Apply promotional codes for bonus credits
 */

import { useState } from 'react'
import { Gift, Sparkles, Rocket, Zap, Award, Check, X, Loader2, Tag, Code, BatteryCharging } from 'lucide-react'
import { applyPromoCode, getCredits } from '@services/stripe'
import { useStore } from '@store/useStore'

const ICONS = {
  gift: Gift,
  sparkles: Sparkles,
  rocket: Rocket,
  zap: Zap,
  award: Award,
  code: Code,
  'battery-charging': BatteryCharging,
}

export default function PromoCode({ onSuccess, compact = false }) {
  const [code, setCode] = useState('')
  const [status, setStatus] = useState('idle') // idle, loading, success, error
  const [result, setResult] = useState(null)
  const setCredits = useStore(state => state.setCredits)

  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!code.trim() || status === 'loading') return

    setStatus('loading')

    // Small delay for UX
    await new Promise(r => setTimeout(r, 500))

    const response = applyPromoCode(code)
    setResult(response)

    if (response.success) {
      setStatus('success')
      setCode('')
      // Force update Zustand store with new balance
      setCredits(response.newBalance)
      onSuccess?.(response)

      // Reset after 3s
      setTimeout(() => {
        setStatus('idle')
        setResult(null)
      }, 3000)
    } else {
      setStatus('error')
      // Reset after 2s
      setTimeout(() => {
        setStatus('idle')
      }, 2000)
    }
  }

  const IconComponent = result?.icon ? ICONS[result.icon] : Gift

  if (compact) {
    return (
      <div className="space-y-2">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="NOEL2024"
              disabled={status === 'loading' || status === 'success'}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800/80 border border-slate-700/50 rounded-lg text-sm focus:outline-none focus:border-primary-500 text-white placeholder-slate-500 font-mono uppercase tracking-wider disabled:opacity-50"
            />
          </div>
          <button
            type="submit"
            disabled={!code.trim() || status === 'loading' || status === 'success'}
            className="px-4 py-2.5 bg-accent-600 hover:bg-accent-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-all"
          >
            {status === 'loading' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : status === 'success' ? (
              <Check className="w-4 h-4" />
            ) : (
              'Appliquer'
            )}
          </button>
        </form>

        {result && (
          <div className={`flex items-center gap-2 text-sm ${
            result.success ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {result.success ? (
              <Check className="w-4 h-4" />
            ) : (
              <X className="w-4 h-4" />
            )}
            {result.message}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-700/50">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-500/20 to-pink-500/20 flex items-center justify-center">
          <Gift className="w-5 h-5 text-accent-400" />
        </div>
        <div>
          <h3 className="font-medium text-white">Code Promo</h3>
          <p className="text-xs text-slate-400">Entrez votre code pour des crédits bonus</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {status === 'success' && result ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4 animate-bounce">
              <IconComponent className="w-8 h-8 text-emerald-400" />
            </div>
            <h4 className="text-lg font-medium text-emerald-400 mb-2">{result.message}</h4>
            <p className="text-slate-400">
              +{result.credits} crédits ajoutés
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Nouveau solde : {result.newBalance} crédits
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Votre code</label>
              <div className="relative">
                <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase())
                    if (status === 'error') setStatus('idle')
                  }}
                  placeholder="Entrez votre code..."
                  disabled={status === 'loading'}
                  className="w-full pl-12 pr-4 py-4 bg-slate-900/50 border border-slate-700/50 rounded-lg focus:outline-none focus:border-accent-500 text-white placeholder-slate-500 font-mono text-lg uppercase tracking-widest disabled:opacity-50"
                  autoComplete="off"
                />
              </div>

              {status === 'error' && result && (
                <p className="text-sm text-red-400 mt-2 flex items-center gap-1">
                  <X className="w-4 h-4" />
                  {result.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!code.trim() || status === 'loading'}
              className="w-full py-4 bg-gradient-to-r from-accent-500 to-pink-500 hover:from-accent-400 hover:to-pink-400 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-semibold transition-all shadow-lg shadow-accent-500/25"
            >
              {status === 'loading' ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Vérification...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Gift className="w-5 h-5" />
                  Appliquer le code
                </span>
              )}
            </button>

            {/* Hints */}
            <div className="text-center space-y-1">
              <p className="text-xs text-slate-500">
                <span className="text-accent-400 font-mono">DEVMODE</span> → 100 crédits (dev)
              </p>
              <p className="text-xs text-slate-500">
                <span className="text-accent-400 font-mono">REFILL50</span> → 50 crédits
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
